"use client";
import Link from "next/link";
import Icon from "@/components/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useIsMobile from "@/hooks/useIsMobile";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import { cn } from "@/lib/utils";

interface PromoBannerTooltipProps {
  htmlContent?: string;
  discountCode?: string;
  endDate?: string;
  learnMoreLink?: string;
  iconColor?: string;
  size?: number;
  className?: string;
}

export default function PromoBannerTooltip({
  htmlContent,
  discountCode,
  endDate,
  learnMoreLink,
  iconColor,
  size = 24,
  className,
}: PromoBannerTooltipProps) {
  const isMobile = useIsMobile();
  const tooltipIconColor = iconColor ?? "text-primary-foreground";

  const tooltipContent = htmlContent ? (
    sanitizeAndParseInlineHTML(htmlContent, true)
  ) : (
    <>
      Offer is valid until {endDate}, at 11:59 p.m. Eastern Time (U.S.). Offer
      does not apply to degree programs. Offer cannot be combined with other
      discount codes. To redeem the offer for verified courses, Professional
      Certificates, XSeries, MicroBachelors® Programs, and MicroMasters®
      Programs, use code {discountCode} at checkout on{" "}
      <Link href="/" className="underline">
        edX.org
      </Link>
      .
      {learnMoreLink && (
        <span>
          {" "}
          <a href={learnMoreLink} className="underline">
            Learn more.
          </a>
        </span>
      )}
    </>
  );

  return (
    <Popover>
      <PopoverTrigger aria-label="More information">
        <div className={tooltipIconColor}>
          <Icon type="info" svgProps={{ width: size, height: size }} />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side={isMobile ? "bottom" : "right"}
        className={cn(
          "bg-primary text-primary-foreground rounded-xl",
          className,
        )}
      >
        {tooltipContent}
      </PopoverContent>
    </Popover>
  );
}
